import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Search, UserPlus, Upload, Download, MessageCircle, Send, User, Trash2, X, Loader2, ChevronLeft, ChevronRight, FileSpreadsheet,
} from 'lucide-react';
import { whatsappAPI } from './whatsappAPI';
import { useOrganizacao, Chip } from './organizacao';
import NovaMensagemModal from './NovaMensagemModal';
import ClientQuickViewModal from '../../components/ClientQuickViewModal';

/* Central de Contatos: todo mundo com quem a empresa fala ou pode falar, num lugar só.
 * Junta conversas do WhatsApp, clientes do cadastro e leads anotados à mão (ou importados),
 * reconhecendo a mesma pessoa pelo telefone. */

const AVATAR = ['#0f766e', '#b45309', '#4338ca', '#be185d', '#15803d', '#0369a1', '#7c3aed', '#c2410c'];
const hash = (s = '') => [...s].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7);
const iniciais = (n = '?') => n.replace(/[^\p{L}\s]/gu, '').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
const fmtTel = (n = '') => {
  const d = n.replace(/\D/g, '');
  const l = d.startsWith('55') && d.length >= 12 ? d.slice(2) : d;
  return l.length >= 10 ? `(${l.slice(0, 2)}) ${l.slice(2, -4)}-${l.slice(-4)}` : (n || '—');
};
const quando = (iso) => {
  if (!iso) return null;
  const dias = Math.floor((Date.now() - Date.parse(iso)) / 864e5);
  if (dias <= 0) return 'hoje';
  if (dias === 1) return 'ontem';
  if (dias < 30) return `há ${dias} dias`;
  if (dias < 365) return `há ${Math.floor(dias / 30)} ${Math.floor(dias / 30) === 1 ? 'mês' : 'meses'}`;
  return new Date(iso).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
};
const ORIGEM = { whatsapp: 'WhatsApp', cadastro: 'Cadastro', manual: 'Anotado à mão' };
const errMsg = (e, p) => e?.response?.data?.detail || p;

export default function ContatosWA() {
  const navigate = useNavigate();
  const { etiquetas, setores } = useOrganizacao();
  const etMap = useMemo(() => Object.fromEntries(etiquetas.map(e => [e.id, e])), [etiquetas]);
  const [f, setF] = useState({ tipo: 'todos', q: '', etiqueta: '', setor: '', responsavel: '', sem_conversa: false, ordem: 'atividade' });
  const [qDigitado, setQDigitado] = useState('');
  const [pagina, setPagina] = useState(1);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [novoLead, setNovoLead] = useState(false);
  const [importar, setImportar] = useState(false);
  const [conversarCom, setConversarCom] = useState(null);
  const [verCliente, setVerCliente] = useState(null);

  useEffect(() => { const t = setTimeout(() => { setF(x => ({ ...x, q: qDigitado })); setPagina(1); }, 300); return () => clearTimeout(t); }, [qDigitado]);
  const carregar = async () => {
    setCarregando(true);
    try { const { data } = await whatsappAPI.central({ ...f, pagina }); setDados(data); }
    catch (e) { toast.error(errMsg(e, 'Não foi possível carregar os contatos')); }
    finally { setCarregando(false); }
  };
  useEffect(() => { carregar(); }, [f, pagina]); // eslint-disable-line react-hooks/exhaustive-deps
  const setFiltro = (k) => (e) => { const v = e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e; setF(x => ({ ...x, [k]: v })); setPagina(1); };

  const exportar = async () => {
    try {
      const { data } = await whatsappAPI.centralCsv(f);
      const url = URL.createObjectURL(data);
      const a = document.createElement('a'); a.href = url; a.download = `contatos_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error('Não foi possível exportar'); }
  };
  const excluirLead = async (c) => {
    if (!window.confirm(`Excluir o lead ${c.nome}?`)) return;
    try { await whatsappAPI.contatoDelete(c.contato_id); toast.success('Lead excluído'); carregar(); }
    catch (e) { toast.error(errMsg(e, 'Não foi possível excluir')); }
  };

  const cont = dados?.contagem || {};
  const totalPag = dados ? Math.max(1, Math.ceil(dados.total / dados.por_pagina)) : 1;
  const selectCls = 'h-9 px-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm outline-none';

  return (
    <div className="space-y-4" data-testid="wa-contatos-page">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <h2 className="text-xl font-bold">Central de Contatos</h2>
          <p className="text-sm text-muted-foreground">Clientes do cadastro, conversas do WhatsApp e leads anotados, reconhecidos pelo telefone.</p>
        </div>
        <button onClick={() => setNovoLead(true)} className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium flex items-center gap-1.5" data-testid="wa-central-novo-lead">
          <UserPlus className="w-4 h-4" /> Novo lead
        </button>
        <button onClick={() => setImportar(true)} className="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium flex items-center gap-1.5 hover:bg-slate-50 dark:hover:bg-slate-800" data-testid="wa-central-importar">
          <Upload className="w-4 h-4" /> Importar planilha
        </button>
        <button onClick={exportar} className="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium flex items-center gap-1.5 hover:bg-slate-50 dark:hover:bg-slate-800" data-testid="wa-central-exportar">
          <Download className="w-4 h-4" /> Exportar
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="px-4 pt-3 flex gap-5 border-b border-slate-200 dark:border-slate-800 text-sm">
          {[['todos', 'Todos', cont.todos], ['cliente', 'Clientes', cont.cliente], ['lead', 'Leads', cont.lead]].map(([k, l, n]) => (
            <button key={k} onClick={() => setFiltro('tipo')(k)} data-testid={`wa-central-tipo-${k}`}
              className={`pb-2.5 -mb-px border-b-2 font-medium flex items-center gap-1.5 ${f.tipo === k ? 'border-emerald-600 text-emerald-700 dark:text-emerald-400' : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'}`}>
              {l}{n != null && <span className="text-xs tabular-nums text-slate-400">{n}</span>}
            </button>
          ))}
        </div>
        <div className="p-3 flex flex-wrap items-center gap-2 border-b border-slate-100 dark:border-slate-800">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input value={qDigitado} onChange={e => setQDigitado(e.target.value)} placeholder="Nome, telefone, CPF, cidade ou observação" data-testid="wa-central-busca"
              className="w-full h-9 pl-9 pr-3 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30" />
          </div>
          {etiquetas.length > 0 && (
            <select value={f.etiqueta} onChange={setFiltro('etiqueta')} className={selectCls} data-testid="wa-central-etiqueta">
              <option value="">Etiqueta</option>{etiquetas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          )}
          {setores.length > 0 && (
            <select value={f.setor} onChange={setFiltro('setor')} className={selectCls}>
              <option value="">Setor</option>{setores.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          )}
          {dados?.responsaveis?.length > 0 && (
            <select value={f.responsavel} onChange={setFiltro('responsavel')} className={selectCls} data-testid="wa-central-responsavel">
              <option value="">Responsável</option>{dados.responsaveis.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          )}
          <select value={f.ordem} onChange={setFiltro('ordem')} className={selectCls} data-testid="wa-central-ordem">
            <option value="atividade">Atividade recente</option>
            <option value="sem_contato">Mais tempo sem contato</option>
            <option value="nome">Nome</option>
          </select>
          <label className="flex items-center gap-1.5 text-sm cursor-pointer px-1">
            <input type="checkbox" checked={f.sem_conversa} onChange={setFiltro('sem_conversa')} className="w-4 h-4 accent-emerald-600" /> Nunca conversou
          </label>
        </div>

        <div className={`transition-opacity ${carregando ? 'opacity-60' : ''}`}>
          {!dados ? (
            <div className="py-16 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : !dados.itens.length ? (
            <div className="py-14 text-center text-sm text-slate-500">
              Nenhum contato com esses filtros.
              {f.tipo === 'lead' && <p className="mt-1">Anote um lead novo ou importe uma planilha.</p>}
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="wa-central-tabela">
              <thead className="hidden md:table-header-group text-left text-xs text-slate-500">
                <tr className="border-b border-slate-100 dark:border-slate-800">
                  <th className="font-medium px-4 py-2">Contato</th>
                  <th className="font-medium px-3 py-2">Telefone</th>
                  <th className="font-medium px-3 py-2">Etiquetas</th>
                  <th className="font-medium px-3 py-2">Responsável</th>
                  <th className="font-medium px-3 py-2">Último contato</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {dados.itens.map(c => {
                  const ets = (c.etiquetas || []).map(id => etMap[id]).filter(Boolean);
                  return (
                    <tr key={c.chave} className="border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50/70 dark:hover:bg-slate-800/40 flex flex-wrap md:table-row px-4 py-2 md:p-0" data-testid={`wa-central-linha-${c.chave}`}>
                      <td className="md:px-4 md:py-2.5 w-full md:w-auto">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="w-9 h-9 rounded-full text-white text-xs font-semibold flex items-center justify-center shrink-0" style={{ background: AVATAR[hash(c.nome) % AVATAR.length] }}>{iniciais(c.nome)}</span>
                          <div className="min-w-0">
                            <p className="font-medium truncate flex items-center gap-2">
                              {c.nome || fmtTel(c.numero)}
                              <span className={`text-[10.5px] px-1.5 rounded-full font-medium shrink-0 ${c.tipo === 'cliente' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200' : 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200'}`}>{c.tipo}</span>
                            </p>
                            <p className="text-xs text-slate-500 truncate">
                              {[c.cidade && `${c.cidade}${c.estado ? `/${c.estado}` : ''}`, ORIGEM[c.origem], c.observacoes].filter(Boolean).join(' — ')}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="md:px-3 md:py-2.5 text-slate-600 dark:text-slate-300 whitespace-nowrap pl-12 md:pl-3 text-xs md:text-sm">{fmtTel(c.numero)}</td>
                      <td className="md:px-3 md:py-2.5 hidden md:table-cell">
                        <div className="flex flex-wrap gap-1 max-w-[220px]">
                          {ets.slice(0, 3).map(e => <Chip key={e.id} cor={e.cor} small>{e.nome}</Chip>)}
                          {ets.length > 3 && <span className="text-[11px] text-slate-500">+{ets.length - 3}</span>}
                        </div>
                      </td>
                      <td className="md:px-3 md:py-2.5 hidden md:table-cell text-slate-600 dark:text-slate-300">{c.responsavel || <span className="text-slate-400">—</span>}</td>
                      <td className="md:px-3 md:py-2.5 text-slate-500 whitespace-nowrap text-xs md:text-sm pl-3">{quando(c.ultimo_contato) || <span className="text-slate-400">nunca</span>}</td>
                      <td className="w-full md:w-auto pl-10 md:pl-3 md:px-3 md:py-2.5 mt-1 md:mt-0">
                        <div className="flex items-center justify-start md:justify-end gap-1">
                          {c.jid ? (
                            <button onClick={() => navigate(`/whatsapp?jid=${encodeURIComponent(c.jid)}`)} title="Abrir conversa" data-testid={`wa-central-abrir-${c.chave}`}
                              className="h-8 px-2.5 rounded-lg text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 flex items-center gap-1.5 text-[13px] font-medium">
                              <MessageCircle className="w-4 h-4" /><span className="hidden lg:inline">Conversa</span>
                            </button>
                          ) : (
                            <button onClick={() => setConversarCom({ nome: c.nome, telefone: c.numero })} title="Iniciar conversa" data-testid={`wa-central-iniciar-${c.chave}`}
                              className="h-8 px-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-1.5 text-[13px] font-medium">
                              <Send className="w-4 h-4" /><span className="hidden lg:inline">Chamar</span>
                            </button>
                          )}
                          {c.cliente_id && (
                            <button onClick={() => setVerCliente(c.cliente_id)} title="Ver cadastro" className="h-8 w-8 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center text-slate-500">
                              <User className="w-4 h-4" />
                            </button>
                          )}
                          {c.origem === 'manual' && c.contato_id && (
                            <button onClick={() => excluirLead(c)} title="Excluir lead" className="h-8 w-8 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/40 flex items-center justify-center text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {dados && dados.total > dados.por_pagina && (
          <div className="px-4 py-2.5 flex items-center justify-between text-sm text-slate-500 border-t border-slate-100 dark:border-slate-800">
            <span>{(pagina - 1) * dados.por_pagina + 1}–{Math.min(pagina * dados.por_pagina, dados.total)} de {dados.total}</span>
            <div className="flex gap-1">
              <button disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)} className="h-8 w-8 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 flex items-center justify-center" aria-label="Página anterior"><ChevronLeft className="w-4 h-4" /></button>
              <button disabled={pagina >= totalPag} onClick={() => setPagina(p => p + 1)} className="h-8 w-8 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 flex items-center justify-center" aria-label="Próxima página"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>

      {novoLead && <NovoLeadModal onClose={() => setNovoLead(false)} onCriado={(lead, conversar) => { setNovoLead(false); carregar(); if (conversar) setConversarCom({ nome: lead.nome, telefone: lead.numero }); }} />}
      {importar && <ImportarModal onClose={() => setImportar(false)} onFeito={carregar} />}
      {conversarCom && (
        <NovaMensagemModal contatoPreSelecionado={conversarCom} onClose={() => setConversarCom(null)}
          onSent={(jid) => { setConversarCom(null); navigate(`/whatsapp?jid=${encodeURIComponent(jid)}`); }} />
      )}
      {verCliente && <ClientQuickViewModal clientId={verCliente} onClose={() => setVerCliente(null)} />}
    </div>
  );
}

function Modal({ titulo, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()} role="dialog" aria-label={titulo}>
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
          <h3 className="font-semibold">{titulo}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Fechar"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function NovoLeadModal({ onClose, onCriado }) {
  const [f, setF] = useState({ nome: '', numero: '', observacoes: '' });
  const [busy, setBusy] = useState(false);
  const inputCls = 'mt-1 w-full h-9 px-3 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30';
  const salvar = async (conversar) => {
    setBusy(true);
    try { const { data } = await whatsappAPI.contatoCreate({ ...f, tags: [] }); toast.success('Lead anotado'); onCriado(data, conversar); }
    catch (e) { toast.error(errMsg(e, 'Não foi possível salvar')); }
    finally { setBusy(false); }
  };
  return (
    <Modal titulo="Novo lead" onClose={onClose}>
      <div className="px-5 py-4 space-y-3">
        <label className="block text-sm font-medium">Nome<input autoFocus value={f.nome} onChange={e => setF({ ...f, nome: e.target.value })} className={inputCls} data-testid="wa-lead-nome" /></label>
        <label className="block text-sm font-medium">Telefone com DDD<input value={f.numero} onChange={e => setF({ ...f, numero: e.target.value })} placeholder="(65) 99999-0000" className={inputCls} data-testid="wa-lead-telefone" /></label>
        <label className="block text-sm font-medium">Observações
          <textarea rows={3} value={f.observacoes} onChange={e => setF({ ...f, observacoes: e.target.value })} placeholder="Ex.: conheceu na feira, quer PRONAF para trator"
            className="mt-1 w-full px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30" />
        </label>
      </div>
      <div className="px-5 py-3 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-2">
        <button onClick={() => salvar(false)} disabled={busy} className="h-9 px-4 rounded-lg border border-slate-200 dark:border-slate-700 text-sm disabled:opacity-50" data-testid="wa-lead-salvar">Salvar</button>
        <button onClick={() => salvar(true)} disabled={busy} className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1.5">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Salvar e chamar no WhatsApp
        </button>
      </div>
    </Modal>
  );
}

function ImportarModal({ onClose, onFeito }) {
  const [arquivo, setArquivo] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const enviar = async () => {
    if (!arquivo) return;
    setBusy(true);
    try {
      const texto = await arquivo.text();
      const { data } = await whatsappAPI.importarCsv(texto);
      setRes(data); onFeito();
    } catch (e) { toast.error(errMsg(e, 'Não foi possível importar')); }
    finally { setBusy(false); }
  };
  return (
    <Modal titulo="Importar leads de planilha" onClose={onClose}>
      <div className="px-5 py-4 space-y-3 text-sm">
        <p className="text-slate-600 dark:text-slate-300">Salve a planilha como <b>CSV</b> com as colunas <b>nome</b>, <b>telefone</b> e, se quiser, <b>observações</b>, nessa ordem. Quem já está na central (pelo telefone) é ignorado.</p>
        <label className="flex items-center gap-3 p-3 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 cursor-pointer hover:border-emerald-400">
          <FileSpreadsheet className="w-8 h-8 text-emerald-600 shrink-0" />
          <span className="min-w-0">
            <span className="font-medium block truncate">{arquivo ? arquivo.name : 'Escolher arquivo .csv'}</span>
            <span className="text-xs text-slate-500">Separado por ponto e vírgula ou vírgula</span>
          </span>
          <input type="file" accept=".csv,text/csv" className="sr-only" onChange={e => { setArquivo(e.target.files?.[0] || null); setRes(null); }} data-testid="wa-importar-arquivo" />
        </label>
        {res && (
          <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/40 px-3 py-2 text-emerald-900 dark:text-emerald-100" data-testid="wa-importar-resultado">
            <b>{res.importados}</b> lead{res.importados === 1 ? '' : 's'} importado{res.importados === 1 ? '' : 's'}
            {res.repetidos ? `, ${res.repetidos} já existia${res.repetidos === 1 ? '' : 'm'}` : ''}
            {res.linhas_invalidas?.length ? <span className="block text-xs text-amber-800 dark:text-amber-300 mt-0.5">Linhas sem nome ou telefone válido: {res.linhas_invalidas.join(', ')}</span> : null}
          </div>
        )}
      </div>
      <div className="px-5 py-3 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-2">
        <button onClick={onClose} className="h-9 px-4 rounded-lg border border-slate-200 dark:border-slate-700 text-sm">{res ? 'Fechar' : 'Cancelar'}</button>
        {!res && (
          <button onClick={enviar} disabled={!arquivo || busy} className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1.5" data-testid="wa-importar-enviar">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Importar
          </button>
        )}
      </div>
    </Modal>
  );
}
